import { Activity, Dataset, ExpressionType, LinkedService, Pipeline } from 'shell-models-lib';
import {
    IdentifierType,
    createPkgValDesc,
    createRefValDesc,
    ValueDescription,
    SymbolTable,
    globalSymbolTable,
    createOverloadedFunValDesc,
    createFunValDesc,
    createSymbolTable,
    createFunRetDesc,
    PackageDescription
} from 'monaco-azure-logic-app-lang';
import { GetMetaActivityDescriptionBuilder } from 'app/services/azure-logic-app-lang/get-meta-act-desc-buldr';
import { getActivityAncestry } from 'app/model/expressions/expression-utils';
import { ModelService } from 'app/model/services/model.service';
import { convertStringToExpressionType } from 'app/model/expressions/expression-resolution';
import { LookupActivity } from 'app/model/pipeline/lookup.activity';
import { GetMetadataActivity } from 'app/model/pipeline/get-metadata.activity';
import { FilterActivity } from 'app/model/pipeline/filter.activity';
import { ExecutePipelineActivity } from 'app/model/pipeline/execute-pipeline.activity';
import { ForEachActivity } from 'app/model/pipeline/foreach.activity';
import { GlobalVariables } from 'app/services/globals';
import { ActivityOutputInferenceService } from 'app/services/activity-output-inference.service';
import type { LibraryVariablesService } from 'app/services/library-variables.service';

// Library variables
const connectionReferencePkgDesc = createPkgValDesc(
    ['ConnectionType'],
    {
        connectionId: createRefValDesc(['Connection Id'], IdentifierType.String)
    },
    {
        optional: false,
        allowAdditionalAnyProperties: true
    }
);

const itemReferencePkgDesc = createPkgValDesc(
    ['ItemType'],
    {
        itemId: createRefValDesc(['Item Id'], IdentifierType.String),
        workspaceId: createRefValDesc(['Workspace Id'], IdentifierType.String)
    },
    {
        optional: false,
        allowAdditionalAnyProperties: true
    }
);

export function convertExpressionTypeToAzLgcExpIdentifierType(expTyp: ExpressionType): IdentifierType {
    // 'String', 'SecureString', 'Int', 'Float', 'Bool', 'Object', 'Array', 'Any'
    switch (expTyp) {
        case 'String':
        case 'SecureString':
            return IdentifierType.String;
        case 'Int':
        case 'Float':
            return IdentifierType.Number;
        case 'Bool':
            return IdentifierType.Boolean;
        case 'Object':
            return IdentifierType.AnyObject;
        case 'Array':
            return IdentifierType.Array;
        case 'Any':
        default:
            return IdentifierType.Any;
    }
}


export const buildActivityPackage = (getMetaDataActTypeValSet: Set<number>, additionalLookupColumns: string[]) => createPkgValDesc([], {
    defaultActivity: createPkgValDesc(['**Return package activity**', 'Package activity'], {
        output: createRefValDesc(
            [
                'output:any',
                'Activity output'
            ],
            IdentifierType.Any
        )
    }, { allowAdditionalAnyProperties: true }),
    lookupActivity: createPkgValDesc(['Package Lookup activity'], {
        rowItem: createRefValDesc([
            'rowItem:any',
            'data of the first row'
        ], IdentifierType.Any),
        defaultReturnValue: createPkgValDesc(['**Return value of a lookup activity**', 'Lookup activity'], {
            output: createPkgValDesc(
                [
                    'output:any & { count, value }',
                    'Lookup activity output'
                ],
                {
                    count: createRefValDesc([
                        'count:number',
                        'count of rows'
                    ], IdentifierType.Number),
                    value: createRefValDesc([
                        'value:any[]',
                        'array of row data'
                    ], IdentifierType.ARRAY_OF_TYPE(['activityPackage', 'lookupActivity', 'rowItem'], 'row data value'))
                }, { allowAdditionalAnyProperties: true }
            )
        }, { allowAdditionalAnyProperties: true }),
        firstRowOnly: buildLookupFirstRowSection(additionalLookupColumns),
    }),
    getMetaDataActivity: GetMetaActivityDescriptionBuilder.buildPkgValDesc(getMetaDataActTypeValSet)
});


export const pipelineFunRetDescRecordBase: Record<string, ValueDescription> = {
    DataFactory: createRefValDesc(
        [
            __IS_FABRIC_DPE__? 'Workspace ID' : __IS_A365__ ? 'Workspace name' : 'Data factory name',
            `${__IS_FABRIC_DPE__? 'ID' : 'Name'} of the ${__IS_A365__ || __IS_FABRIC_DPE__? 'workspace' : 'data factory'} the pipeline run is running within`
        ],
        IdentifierType.String
    ),
    Pipeline: createRefValDesc([`Pipeline ${__IS_FABRIC_DPE__? 'ID' : 'Name'}`, `${__IS_FABRIC_DPE__? 'ID' : 'Name'} of the pipeline`], IdentifierType.String),
    GroupId: createRefValDesc(['Pipeline group ID', 'ID of the group to which the pipeline run belongs'], IdentifierType.String),
    RunId: createRefValDesc(['Pipeline run ID', 'ID of the specific pipeline run'], IdentifierType.String),
    TriggerId: createRefValDesc(['Pipeline trigger ID', 'ID of the trigger that invokes the pipeline'], IdentifierType.String),
    TriggerTime: createRefValDesc(
        [
            'Pipeline trigger time',
            'Time when the trigger that invoked the pipeline. The trigger time is the actual fired time, not the scheduled time. For example, 13:20:08.0149599Z is returned instead of 13:20:00.00Z'
        ],
        IdentifierType.String
    ),
    TriggerType: createRefValDesc(
        [
            'Pipeline trigger type',
            'Type of the trigger that invoked the pipeline (Manual, Scheduler)'
        ],
        IdentifierType.String
    ),
    TriggeredByPipelineName: createRefValDesc(
        [
            'Pipeline triggered by pipeline name',
            'Name of the pipeline that triggered this pipeline. Applicable when a pipeline run is triggered by an Execute Pipeline activity; Evaluates to Null when used in other circumstances.'
        ],
        IdentifierType.String,
        true
    ),
    TriggeredByPipelineRunId: createRefValDesc(
        [
            'Pipeline triggered by pipeline run ID',
            'Run ID of the pipeline that triggered this pipeline. Applicable when a pipeline run is triggered by an Execute Pipeline activity; Evaluates to Null when used in other circumstances.'
        ],
        IdentifierType.String,
        true
    )
};

export function getTriggerEventFunRetDescRecord(): Record<string, ValueDescription> {
    return {
        Source: createRefValDesc(['Trigger source', 'Event source for trigger'], IdentifierType.String),
        Subject: createRefValDesc(['Trigger subject', 'Event subject for trigger'], IdentifierType.String),
        Type: createRefValDesc(['Trigger type', 'Event type for trigger'], IdentifierType.String),
        FolderPath: createRefValDesc(['Trigger folder path', 'Path to the folder that contains the trigger event file'], IdentifierType.String),
        FileName: createRefValDesc(['Trigger file name', 'File name for trigger event'], IdentifierType.String),
        windowStartTimeUtc: createRefValDesc(['Window start time', 'Window start time for interval-based schedule'], IdentifierType.String),
        windowEndTimeUtc: createRefValDesc(['Window end time', 'Window end time for interval-based schedule'], IdentifierType.String),
    }
}

export function getPipelineFunRetDescRecord(): Record<string, ValueDescription> {
    return !__IS_FABRIC_DPE__? {
        ...pipelineFunRetDescRecordBase,
        TriggerName: createRefValDesc(['Pipeline trigger name', 'Name of the trigger that invokes the pipeline'], IdentifierType.String),
    } : {
        ...pipelineFunRetDescRecordBase,
        TriggeredByPipeline: createRefValDesc(
            [
                'Pipeline triggered by pipeline ID',
                'ID of the pipeline that triggered this pipeline. Applicable when a pipeline run is triggered by an Execute Pipeline activity; Evaluates to Null when used in other circumstances.'
            ],
            IdentifierType.String,
            true
        ),
        PipelineName: createRefValDesc(
            [
                'Pipeline Name',
                'Name of the pipeline'
            ],
            IdentifierType.String,
            true
        )
    };
}

export function getActivityAncestrySync(entity: Pipeline | Dataset | LinkedService, activity: Activity): Activity[] {
    let ancestry = new Array<Activity>();

    if (activity && entity instanceof Pipeline) {
        ancestry = getActivityAncestry(activity);
    }

    return ancestry;
}


export function generateRootSymbolTableSync(
    entity: Pipeline | Dataset | LinkedService | undefined,
    activity: Activity | undefined,
    _modelService: ModelService,
    hideEntityParameters = false,
    showTriggerParameters = false,
    showWorkspaceVariables = false,
    _activityOutputService?: ActivityOutputInferenceService,
    _libraryVariablesService?: LibraryVariablesService
): SymbolTable {
    if (!entity){
        return globalSymbolTable;
    }

    const extBaseDescRecord: Record<string, ValueDescription> = {};
    const funRetDescRecord: Record<string, ValueDescription> = {};
    const pipelineFunRetDescRecord: Record<string, ValueDescription> = getPipelineFunRetDescRecord();
    const triggerEventFunRetDescRecord: Record<string, ValueDescription> = getTriggerEventFunRetDescRecord();

    if (!hideEntityParameters && entity) {
        // account for nested pipelines (execute pipeline and foreach activity children), we want the parameters
        // for the top most concept
        const root = entity.rootOwner;
        const rootConcept = root.concept as Dataset | Pipeline | LinkedService;
        const entityParameters = Array.from(rootConcept ? rootConcept.parameters : []);

        // populate owner's parameters if any
        if (rootConcept && rootConcept.parameters && rootConcept.parameters.size > 0) {
            const paramDescriptor: Record<string, ValueDescription> = {};

            entityParameters.filter((parameter) => parameter && parameter.name).forEach((parameter) => {
                paramDescriptor[parameter.name] = createRefValDesc(
                    [
                        parameter.name,
                        parameter.description
                    ],
                    convertExpressionTypeToAzLgcExpIdentifierType(
                        convertStringToExpressionType(parameter._type)
                    )
                );
            });

            if (entity instanceof Pipeline) {
                pipelineFunRetDescRecord['parameters'] = createPkgValDesc(['Package parameters'], paramDescriptor);
            } else if (entity instanceof Dataset) {
                funRetDescRecord['dataset'] = createPkgValDesc(['**Return package dataset**', 'Package dataset'], paramDescriptor);
            } else {
                funRetDescRecord['linkedService'] = createPkgValDesc(['**Return package linkedService**', 'Package linkedService'], paramDescriptor);
            }
        }

        // populate pipeline's parameters for dataset entity
        if (entity instanceof Dataset){
            pipelineFunRetDescRecord['parameters'] = createRefValDesc(['Package parameters of any type'], IdentifierType.Any);
        }
    }

    if (entity) {
        const root = entity.rootOwner;
        if (root.concept && root.concept instanceof Pipeline) {
            const rootConcept = root.concept;
            const entityVariables = Array.from(rootConcept.variables);

            if (rootConcept.variables && rootConcept.variables.size) {
                // const variablesDescriptor: Record<string, ValueDescription> = {};

                const desc: string[] = [];
                const paraTypes: IdentifierType[][] = [];
                const retTypes: IdentifierType[] = [];

                entityVariables.filter((parameter) => parameter && parameter.name).forEach((variable) => {
                    desc.push(`Variable ${variable.name} ${variable.description || ''}`);
                    paraTypes.push([IdentifierType.CONSTANT(variable.name)]);
                    retTypes.push(convertExpressionTypeToAzLgcExpIdentifierType(
                        convertStringToExpressionType(variable._type)
                    ));
                });

                extBaseDescRecord['variables'] = createOverloadedFunValDesc(desc, paraTypes, retTypes);
            }
        }
    }

    if (showWorkspaceVariables && entity) {
        const root = entity.rootOwner;
        if (root.concept && root.concept instanceof Pipeline) {
            const rootConcept = root.concept;
            const entityLibraryVariables = Array.from(rootConcept.libraryVariables);

            if (rootConcept.libraryVariables && rootConcept.libraryVariables.size) {
                const varDescriptor: Record<string, ValueDescription> = {};
                entityLibraryVariables.filter((variable) => variable && variable.name).forEach((variable) => {
                    const rawType = _libraryVariablesService?.getRawType(variable.libraryName, variable.variableName);
                    if (rawType === 'ConnectionReference') {
                        varDescriptor[variable.name] = connectionReferencePkgDesc;
                    } else if (rawType === 'ItemReference') {
                        varDescriptor[variable.name] = itemReferencePkgDesc;
                    } else {
                        varDescriptor[variable.name] = createRefValDesc(
                            [variable.name, variable.description],
                            convertExpressionTypeToAzLgcExpIdentifierType(convertStringToExpressionType(variable._type))
                        );
                    }
                });
                pipelineFunRetDescRecord['libraryVariables'] = createPkgValDesc(['Library variables'], varDescriptor); 
            }
        }
    }

    // TODO: we should handle showing nested activities within ForEach activities. There are caveats for this,
    // nested activity outputs can only be consumed by fields of the same nesting level.
    if (entity instanceof Pipeline && activity) {
        const ancestry = getActivityAncestrySync(entity, activity);

        if (ancestry && ancestry.length > 0) {
            const desc: string[] = ['Activity output'];
            // const retTyp = IdentifierType.FUNCTION_RETURN_TYPE(['activity']);
            const retTypes: IdentifierType[] = [];
            const getMetaDataActTypeValSet: Set<number> = new Set();
            const additionalLookupExpressions: string[] = [];
            const paraTypes = Array.from(ancestry).map((activity) => {
                desc.push(`${activity.name} ${stringResources.ExpressionBuilder_ActivityOutput}`);
                if (activity instanceof LookupActivity) {
                    // LookupActivity.firstRowOnly is a tri-state property and by default it would be activated even undefined
                    if (activity.firstRowOnly !== false) {
                        retTypes.push(IdentifierType.FUNCTION_RETURN_TYPE(['activityPackage', 'lookupActivity', 'firstRowOnly'], 'First-row-only lookup activity return type'));
                        if (GlobalVariables.enableActivityOutputInference && (!!_activityOutputService)) {
                            additionalLookupExpressions.push(..._activityOutputService.generateLookupColumns(activity));
                        }
                    } else {
                        retTypes.push(IdentifierType.FUNCTION_RETURN_TYPE(['activityPackage', 'lookupActivity', 'defaultReturnValue'], 'Lookup activity return type'));
                    }
                } else if (activity instanceof GetMetadataActivity) {
                    const curGetMetadataActBitwiseFields = GetMetaActivityDescriptionBuilder.getMetadataActivityFileDatasetBitwiseFields(activity);
                    if (curGetMetadataActBitwiseFields) {
                        getMetaDataActTypeValSet.add(curGetMetadataActBitwiseFields);
                        retTypes.push(IdentifierType.FUNCTION_RETURN_TYPE(
                            ['activityPackage', 'getMetaDataActivity', GetMetaActivityDescriptionBuilder.buildSubVdKey(curGetMetadataActBitwiseFields)],
                            'GetMetaData activity return type')
                        );
                    } else {
                        retTypes.push(IdentifierType.FUNCTION_RETURN_TYPE(
                            ['activityPackage', 'defaultActivity'],
                            'Default getMetaData activity return type'
                        ));
                    }
                } else {
                    retTypes.push(IdentifierType.FUNCTION_RETURN_TYPE(['activityPackage', 'defaultActivity'], 'Activity return type'));
                }
                return [IdentifierType.CONSTANT('' + activity.name)];
            });

            extBaseDescRecord['activity'] = createOverloadedFunValDesc(
                desc,
                paraTypes,
                retTypes
            );
            funRetDescRecord['activityPackage'] = buildActivityPackage(getMetaDataActTypeValSet, additionalLookupExpressions);
        }

        if (activity instanceof FilterActivity) {
            extBaseDescRecord['item'] = createFunValDesc(
                [
                    `${activity.name} item`,
                    `${stringResources.ExpressionBuilder_CurrentItem}`
                ],
                [],
                IdentifierType.Any
            );
        } else {
            // Add item() to nested activities if the nearest ForEach activity ancestry found
            let oneOwner = entity.owner;
            while (oneOwner) {
                if (oneOwner instanceof ExecutePipelineActivity) {
                    break;
                }
                if (oneOwner instanceof ForEachActivity) {
                    extBaseDescRecord['item'] = createFunValDesc(
                        [
                            `${oneOwner.name} item`,
                            `${stringResources.ExpressionBuilder_CurrentItem}`
                        ],
                        [],
                        IdentifierType.Any
                    );
                    break;
                }
                oneOwner = oneOwner.owner;
            }
        }
    }

    if (GlobalVariables.enableGlobalParameters && entity instanceof Pipeline) {
        const rootConcept = _modelService.factory;
        const entityParameters = Array.from(rootConcept ? rootConcept.parameters : []);
        if (rootConcept && rootConcept.parameters && rootConcept.parameters.size > 0) {
            const globalParametersDescriptor: Record<string, ValueDescription> = {};
            entityParameters.filter((parameter) => parameter && parameter.name).forEach((parameter) => {
                globalParametersDescriptor[parameter.name] = createRefValDesc(
                    [
                        parameter.name,
                        parameter.description
                    ],
                    convertExpressionTypeToAzLgcExpIdentifierType(
                        convertStringToExpressionType(parameter._type)
                    )
                );
            });
            pipelineFunRetDescRecord['globalParameters'] = createPkgValDesc(['Package globalParameters'], globalParametersDescriptor);
        }
    }

    if (__IS_FABRIC_DPE__&& showTriggerParameters) {
        pipelineFunRetDescRecord['TriggerEvent'] = createPkgValDesc(['Package trigger event'], triggerEventFunRetDescRecord);
    }

    funRetDescRecord['pipeline'] = createPkgValDesc(['**Return package pipeline**', 'Package pipeline'], pipelineFunRetDescRecord);

    ['pipeline', 'dataset', 'linkedService'].forEach((one) => {
        if (one in funRetDescRecord) {
            extBaseDescRecord[one] = createFunValDesc(
                [`**${one}()**`, `Return ${one} object`],
                [],
                IdentifierType.FUNCTION_RETURN_TYPE([one])
            );
        }
    });

    return createSymbolTable(extBaseDescRecord, createFunRetDesc(createPkgValDesc([], funRetDescRecord)));
}

function buildLookupFirstRowSection(additionalColumns: string[]): PackageDescription {
    const pkgDefault = createPkgValDesc(['**Return value of a lookup activity[firstRowOnly]**', 'Lookup activity for the first only'], {
        output: createPkgValDesc(
            [
                'output:any &{ firstRow }',
                'First-row-only lookup activity output'
            ],
            {
                firstRow: createRefValDesc([
                    'firstRow:any',
                    'data of the first row'
                ], IdentifierType.Any)
            }, { allowAdditionalAnyProperties: true }
        )
    }, { allowAdditionalAnyProperties: true })

    if (additionalColumns.length == 0) {
        return pkgDefault;
    }

    const additionalExpressionDescriptor: Record<string, ValueDescription> = {};
    for(const e of additionalColumns) {
        additionalExpressionDescriptor[e] = createRefValDesc([e], IdentifierType.Any);
    }

    const pkgSuggestions = createPkgValDesc([], {
        output: createPkgValDesc(
            [
                'output:any &{ firstRow }',
                'First-row-only lookup activity output'
            ],
            {
                firstRow: createPkgValDesc(['Suggested columns'], 
                    additionalExpressionDescriptor, 
                    { allowAdditionalAnyProperties: true }
                ),
            }, { allowAdditionalAnyProperties: true }
        )
    }, { allowAdditionalAnyProperties: true })

    return pkgSuggestions;
}
